// Login page component for InfluenceHub MVP
// Handles user authentication for both brands and influencers
// Includes Firebase Auth integration and error handling

import React, { useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig.js';
import AuthLayout from '../components/auth/AuthLayout';
import GoogleAuthButton from '../components/auth/GoogleAuthButton';
import { getPostAuthRouteWithLazyMigration } from '../lib/getPostAuthRoute';
import { resolveLoginDestination } from '../lib/routing/resolveLoginDestination';
import { signInWithGoogle, ensureUserDocFromAuthUser } from '../lib/auth';
import {
  needsBackfill,
  backfillOnboardingCompleted,
  isLegacyUser,
} from '../lib/onboardingLegacy';
import { ensureUserDoc } from '../lib/firestore/users';

// localStorage keys for Remember Me functionality (email only — never store password)
const REMEMBER_ME_KEY = 'influencehub_remember_me';
const REMEMBER_ME_EMAIL_KEY = 'influencehub_remember_me_email';

const Login = ({ onNavigate }) => {
  // Form state management
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  // UI state management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // Initialize state from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Security: remove any previously stored password (we no longer store passwords)
    try {
      localStorage.removeItem('influencehub_remember_me_password');
    } catch (_) {}

    const storedRemember = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
    const storedEmail = storedRemember
      ? localStorage.getItem(REMEMBER_ME_EMAIL_KEY) || ''
      : '';

    setRememberMe(storedRemember);
    if (storedRemember && storedEmail) {
      setFormData(prev => ({
        ...prev,
        email: storedEmail
        // Password is never stored — user must re-enter for security
      }));
      if (process.env.NODE_ENV === 'development') {
        console.log('[Login] Loaded rememberMe from storage:', storedRemember);
        console.log('[Login] Loaded remembered email:', storedEmail ? '***' + storedEmail.slice(-10) : '(none)');
      }
    }
  }, []);

  // Check if user is already logged in (from previous session with local persistence)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;

      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
      if (currentPath === '/') {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ User already logged in, redirecting...');
        }
        try {
          const snap = await getDoc(doc(db, 'users', user.uid));
          let data = snap.exists() ? snap.data() : null;
          if (!snap.exists() && isLegacyUser(user)) {
            await ensureUserDoc(user.uid, user.email || null);
            await backfillOnboardingCompleted(user.uid);
            const refetch = await getDoc(doc(db, 'users', user.uid));
            data = refetch.exists() ? refetch.data() : null;
          } else if (snap.exists() && needsBackfill(user, data)) {
            await backfillOnboardingCompleted(user.uid);
            const refetch = await getDoc(doc(db, 'users', user.uid));
            data = refetch.exists() ? refetch.data() : null;
          }
          const postAuthRoute = await getPostAuthRouteWithLazyMigration(user.uid, data, user);
          const route = resolveLoginDestination(window.location.search || '', postAuthRoute, data, user);
          onNavigate(route, null, { replace: true });
        } catch {
          onNavigate('/select-role', null, { replace: true });
        }
      }
    });

    return () => unsubscribe();
  }, [onNavigate]);

  // Handle Remember Me checkbox change
  const handleRememberMeChange = (e) => {
    const newValue = e.target.checked;
    setRememberMe(newValue);

    if (newValue) {
      // Checkbox checked: save preference and email only (never password)
      localStorage.setItem(REMEMBER_ME_KEY, 'true');
      if (formData.email.trim()) {
        localStorage.setItem(REMEMBER_ME_EMAIL_KEY, formData.email.trim());
      }
    } else {
      // Checkbox unchecked: clear stored data
      localStorage.removeItem(REMEMBER_ME_KEY);
      localStorage.removeItem(REMEMBER_ME_EMAIL_KEY);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    
    // If Remember Me is checked, update stored email only (never password)
    if (rememberMe && name === 'email' && value.trim()) {
      localStorage.setItem(REMEMBER_ME_EMAIL_KEY, value.trim());
    }
  };

  // Form validation
  const validateForm = () => {
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.password.trim()) {
      setError('Password is required');
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Attempting login for:', formData.email);
        console.log('🔐 Remember Me checkbox state:', rememberMe);
      }
      
      // Step 1: ALWAYS sign out any existing user first (if any)
      // This is required to change persistence - Firebase only allows persistence changes when no user is signed in
      if (auth.currentUser) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔐 Signing out existing user first to change persistence...');
        }
        await signOut(auth);
        // Wait for auth state to actually clear - this is critical
        await new Promise((resolve) => {
          let resolved = false;
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (!user && !resolved) {
              resolved = true;
              unsubscribe();
              resolve();
            }
          });
          // Timeout after 2 seconds to prevent infinite wait
          setTimeout(() => {
            if (!resolved) {
              resolved = true;
              unsubscribe();
              resolve();
            }
          }, 2000);
        });
      }

      // Step 2: Set persistence based on Remember Me checkbox state
      const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
      
      // Verify currentUser is null before setting persistence
      if (auth.currentUser) {
        console.error('❌ ERROR: currentUser still exists! Cannot set persistence. Aborting login.');
        setError('Unable to change session settings. Please try again.');
        setLoading(false);
        return;
      }

      try {
        // Set persistence BEFORE signing in - this is critical
        await setPersistence(auth, persistence);
        if (process.env.NODE_ENV === 'development') {
          console.log('[Login] Auth persistence set to:', rememberMe ? 'local' : 'session');
        }
        
        // Small delay to ensure persistence is fully applied before sign-in
        await new Promise(resolve => setTimeout(resolve, 200));
      } catch (persistenceError) {
        // Fallback to session persistence if local persistence fails (e.g., private browsing)
        console.warn('⚠️ [Login] Failed to set persistence, falling back to session:', persistenceError);
        try {
          await setPersistence(auth, browserSessionPersistence);
          if (process.env.NODE_ENV === 'development') {
            console.log('[Login] Fallback: Using session persistence');
          }
          // Update localStorage to match what actually worked
          localStorage.removeItem(REMEMBER_ME_KEY);
          localStorage.removeItem(REMEMBER_ME_EMAIL_KEY);
          setRememberMe(false);
        } catch (fallbackError) {
          console.error('❌ [Login] Failed to set fallback persistence:', fallbackError);
          setError('Unable to set session preferences. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Step 3: Authenticate user with Firebase Auth (including admin)
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      
      // Step 4: After successful login, save Remember Me preference and email only (never password)
      if (rememberMe) {
        localStorage.setItem(REMEMBER_ME_KEY, 'true');
        localStorage.setItem(REMEMBER_ME_EMAIL_KEY, formData.email.trim());
        if (process.env.NODE_ENV === 'development') {
          console.log('[Login] Saved Remember Me preference and email to localStorage');
        }
      } else {
        // Clear stored data if Remember Me is not checked
        localStorage.removeItem(REMEMBER_ME_KEY);
        localStorage.removeItem(REMEMBER_ME_EMAIL_KEY);
        if (process.env.NODE_ENV === 'development') {
          console.log('[Login] Cleared Remember Me data from localStorage');
        }
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ User logged in successfully:', userCredential.user.uid);
        console.log('📧 User email:', userCredential.user.email);
      }
      
      // Step 5: Redirect to post-auth destination (with legacy bypass + escape hatch)
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Redirecting to Profile Builder...');
      }
      try {
        const userDocSnap = await getDoc(doc(db, 'users', userCredential.user.uid));
        let data = userDocSnap.exists() ? userDocSnap.data() : null;
        if (userDocSnap.exists() && needsBackfill(userCredential.user, data)) {
          await backfillOnboardingCompleted(userCredential.user.uid);
          const refetch = await getDoc(doc(db, 'users', userCredential.user.uid));
          data = refetch.exists() ? refetch.data() : null;
        }
        const postAuthRoute = await getPostAuthRouteWithLazyMigration(
          userCredential.user.uid,
          data,
          userCredential.user,
        );
        const route = resolveLoginDestination(
          window.location.search || '',
          postAuthRoute,
          data,
          userCredential.user,
        );
        onNavigate(route, null, { replace: true });
      } catch {
        onNavigate('/select-role', null, { replace: true });
      }
      
    } catch (error) {
      // Handle Firebase Auth errors
      // DO NOT clear remembered email on failed attempts
      setLoading(false);
      switch (error.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later');
          break;
        case 'auth/network-request-failed':
          setError('Network error. Please check your connection');
          break;
        default:
          setError('Invalid email or password');
      }
    } finally {
      // Ensure loading state is reset even if navigation doesn't happen immediately
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to continue building on InfluenceHub."
      onHomeNavigate={onNavigate ? () => onNavigate('/') : undefined}
    >
      <div className="mb-6">
        <GoogleAuthButton 
          text="Continue with Google" 
            onClick={async () => {
            try {
              setLoading(true);
              setError('');
              const user = await signInWithGoogle();
              const userDocSnap = await ensureUserDocFromAuthUser(user);
              let data = userDocSnap.exists() ? userDocSnap.data() : null;
              if (needsBackfill(user, data)) {
                await backfillOnboardingCompleted(user.uid);
                const refetch = await getDoc(doc(db, 'users', user.uid));
                data = refetch.exists() ? refetch.data() : null;
              }
              const postAuthRoute = await getPostAuthRouteWithLazyMigration(user.uid, data, user);
              const route = resolveLoginDestination(window.location.search || '', postAuthRoute, data, user);
              onNavigate(route, null, { replace: true });
            } catch (err) {
              setError(err.message || 'Failed to sign in with Google');
              console.error('Google auth error:', err);
              setLoading(false);
            }
          }}
        />
        
        <div className="relative mt-6">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center text-sm font-medium leading-6">
            <span className="bg-white px-4 text-slate-400">or</span>
          </div>
        </div>
      </div>

      <form className="space-y-4" onSubmit={handleSubmit}>
        {/* Email Input */}
        <div>
          <label htmlFor="email" className="text-sm font-medium text-slate-700">
            Email Address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="mt-2 h-11 w-full rounded-md border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            placeholder="Enter your email"
            autoComplete="email"
          />
        </div>

        {/* Password Input */}
        <div>
          <label htmlFor="password" className="text-sm font-medium text-slate-700">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            required
            value={formData.password}
            onChange={handleChange}
            className="mt-2 h-11 w-full rounded-md border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            placeholder="Enter your password"
            autoComplete="current-password"
          />
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between !mb-[15px]">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              checked={rememberMe}
              onChange={handleRememberMeChange}
              className="h-4 w-4 rounded border-slate-300 text-[#1B9DA2] focus:ring-[#1B9DA2]"
            />
            <label htmlFor="remember-me" className="ml-2 text-sm text-slate-700">
              Remember me
            </label>
          </div>

          <div className="text-sm">
            <button
              type="button"
              onClick={() => onNavigate('forgot-password')}
              className="font-medium text-slate-500 transition hover:text-[#7842D6]"
            >
              Forgot password?
            </button>
          </div>
        </div>

        <div className="min-h-[44px]">
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          ) : null}
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            disabled={loading}
            className="h-12 w-full rounded-lg bg-[#1B9DA2] text-sm font-medium text-white transition hover:bg-[#168589] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Signing In...' : 'Sign in'}
          </button>
        </div>

        {/* Signup Link */}
        <div className="text-center">
          <p className="text-sm text-slate-500">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={() => onNavigate('signup')}
              className="font-medium text-slate-500 transition hover:text-[#7842D6]"
            >
              Create one
            </button>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

export default Login;
